import type { Ctx } from '@/core/context';
import { completion } from './production';
import { tr } from '@/i18n';

export type EndingId = 'perfect' | 'normal' | 'safety' | 'food' | 'management' | 'guaiguai';

/** 結局判定（優先序：工安 > 食安 > 管理 > 乖乖傳說 > 完美 > 正常） */
export function evaluate(ctx: Ctx): { id: EndingId; reasons: string[] } {
  const { day, meta } = ctx;
  const reasons: string[] = [];
  const comp = completion(ctx);
  const severe = day.injuries.filter(i => i.severity !== 'light');
  const light = day.injuries.filter(i => i.severity === 'light');
  const cases = day.caseQueue.map(id => day.cases[id]);
  const bad = cases.filter(p => p.grade === 'bad').length;
  const unfinished = cases.filter(p => p.stage !== 'done').length + Math.max(0, day.missionQueue.filter(id => ['main', 'daily'].includes(ctx.c.missionById.get(id)?.type ?? '') && !day.missions[id]?.done).length - 1);
  const overtime = Math.max(0, day.clock - day.shiftEnd);

  if (severe.length || day.flags.caseSafetyIncident || light.length >= 2) {
    if (severe.length) reasons.push(tr("發生{0}", severe.map(i => i.label).join(tr("、"))));
    if (light.length) reasons.push(tr("工傷 {0} 件", light.length));
    if (day.flags.caseSafetyIncident) reasons.push(tr("案件處理不當，造成人員安全事故"));
    return { id: 'safety', reasons };
  }
  if (day.factory.foodSafety < 55 || (day.flags.caseFoodIncident && day.factory.foodSafety < 75)) {
    reasons.push(tr("食安指數 {0}", Math.round(day.factory.foodSafety)));
    if (day.flags.caseFoodIncident) reasons.push(tr("有批次需要重工或報廢"));
    return { id: 'food', reasons };
  }
  if (comp < 0.7 || overtime > 150 || unfinished > 0) {
    if (comp < 0.7) reasons.push(tr("訂單達成率只有 {0}%", Math.round(comp * 100)));
    if (overtime > 150) reasons.push(tr("加班 {0} 分鐘", Math.round(overtime)));
    if (unfinished) reasons.push(tr("還有案件或部門任務沒做完就下班"));
    return { id: 'management', reasons };
  }
  if (day.items.guaiguai && !day.items.spotted && !light.length) {
    reasons.push(tr("綠色乖乖一路守護到下班，駐廠品保完全沒發現"));
    return { id: 'guaiguai', reasons };
  }
  if (!bad && !light.length && !day.factory.caught && !day.factory.unseen && comp >= 0.95 && meta.rewinds === 0) {
    reasons.push(tr("所有案件好結局、零工傷、零違規、零重生、訂單達標"));
    return { id: 'perfect', reasons };
  }
  if (bad) reasons.push(tr("{0} 件案件壞結局", bad));
  if (light.length) reasons.push(tr("有 1 件輕傷"));
  if (day.factory.caught + day.factory.unseen) reasons.push(tr("動線違規 {0} 次", day.factory.caught + day.factory.unseen));
  if (meta.rewinds) reasons.push(tr("用了 {0} 次「我要重新活一次」", meta.rewinds));
  return { id: 'normal', reasons };
}
